// What the meeting recorder needs from the rest of the client (BUILD 1 WP-4).
//
// The capture core, the microphone owner, the device spool, the mint and the
// session socket are WP-3's (C-7, ui/speech/**); the meetings API is WP-0b's.
// The recorder never imports them directly: they arrive through this seam, so
// the recorder's logic is tested against fakes, and the browser wiring
// (meeting-recorder-runtime.ts) is the only file that names the real modules.
// Owner: WP-4.
import type {
  CaptureSource,
  MicOwner,
  MintSpeechSession,
  SpeechSocketApi,
  SpeechSocketOptions,
  SpeechSpool,
  StartCapture,
} from '@/speech/contracts';
import type { meetingsApi } from './meetings-api';

export type MeetingsCaptureApi = Pick<
  typeof meetingsApi,
  | 'get'
  | 'capabilities'
  | 'startRecording'
  | 'putTake'
  | 'putChunk'
  | 'endTake'
  | 'captureState'
  | 'stopCapture'
  | 'discardCapture'
>;

export type CaptureSupport = {
  mic: boolean;
  tab: boolean;
  secure: boolean;
  desktopSystem?: boolean;
};

export type StorageLike = {
  estimate?(): Promise<{ quota?: number; usage?: number }>;
  persist?(): Promise<boolean>;
};

/** A screen wake lock; the browser releases it whenever the page is hidden (`released`). */
export type WakeLockSentinelLike = { release(): Promise<void>; readonly released?: boolean };
export type WakeLockLike = {
  request(type: 'screen'): Promise<WakeLockSentinelLike>;
};

/**
 * One lock per spooled take, held by the tab that owns it (Web Locks in the
 * browser; released by the browser when that tab reloads or dies). Another
 * tab of the same browser sees the same spool and must never recover a take
 * that is still being recorded or drained. `tryHold` resolves null when the
 * lock is held elsewhere; otherwise it resolves the release function.
 */
export type TakeLock = { tryHold(name: string): Promise<(() => void) | null> };

export interface MeetingRecorderDeps {
  startCapture: StartCapture;
  micOwner: MicOwner;
  spool: SpeechSpool;
  mint: MintSpeechSession;
  /** `new SpeechSocket(options)` in the browser. */
  openSocket(options: SpeechSocketOptions): SpeechSocketApi;
  wsUrl(path: string): string;
  captureSupport(): CaptureSupport;
  api: MeetingsCaptureApi;
  sha256(bytes: Uint8Array): Promise<string>;
  storage: StorageLike | null;
  wakeLock: WakeLockLike | null;
  takeLock: TakeLock;
  /** A stable id for this browser profile (a second device gets another one). */
  deviceId(): string;
  uuid(): string;
  /** Phones and tablets: no tab capture there (C-9 record control). */
  isMobile(): boolean;
  now(): number;
  sleep(ms: number): Promise<void>;
  random(): number;
  /** beforeunload and visibilitychange, when a page exists. */
  pageEvents: Pick<EventTarget, 'addEventListener' | 'removeEventListener'> | null;
}

/** The sources a "This room / A meeting tab / Both" choice records. */
export type RecordSourceChoice = 'room' | 'tab' | 'both' | 'system' | 'system-both';
// The tab opens first: the browser's tab picker needs the click's activation,
// which a microphone prompt could otherwise outlast.
export const SOURCES_FOR: Record<RecordSourceChoice, CaptureSource[]> = {
  room: ['mic'],
  tab: ['tab'],
  both: ['tab', 'mic'],
  system: ['system'],
  'system-both': ['system', 'mic'],
};

let installed: MeetingRecorderDeps | null = null;
const listeners = new Set<() => void>();

/** Called once by the browser wiring; tests install fakes. */
export function installMeetingRecorderDeps(deps: MeetingRecorderDeps | null) {
  installed = deps;
  for (const listener of [...listeners]) listener();
}

export const meetingRecorderDeps = (): MeetingRecorderDeps | null => installed;

export function onMeetingRecorderDeps(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
